<!-- Toolbar -->
<div class="hx-managed-table__toolbar">
  <div class="hx-managed-table__toolbar-left">
    <hx-input
      class="hx-managed-table__global-search"
      [disabled]="!hasData()"
      [placeholder]="globalSearchPlaceholder()"
      [startIcon]="'search'"
      [value]="globalSearchValue()"
      (valueChange)="applyGlobalSearch($event.value)" />

    <button
      hxButton
      startItem="sliders"
      type="button"
      [disabled]="!hasData()"
      [size]="'sm'"
      [variant]="'outline'"
      (click)="openColumnCustomization()">
      Column Customization
    </button>
  </div>

  <div class="hx-managed-table__toolbar-right">
    <ng-content select="[toolbarActions]" />
  </div>
</div>

<!-- Main Table -->
@if (hasData()) {
  <hx-table
    class="hx-managed-table"
    [ariaLabel]="ariaLabel()"
    [disabled]="disabled()"
    [enableRowHover]="enableRowHover()"
    [headerVariant]="headerVariant()"
    [maxHeight]="maxHeight()"
    [noDataMessage]="noDataMessage()"
    [rowVariant]="rowVariant()">
    <!-- Table Header -->
    <hx-table-header>
      <!-- Expansion Icon Column Header (only if expandable) -->
      @if (isExpandableRowsAllowed()) {
        <hx-header-cell class="hx-managed-table__expansion-header" [align]="'center'" [sortable]="false" [width]="48">
          <span class="sr-only">Expand/Collapse</span>
        </hx-header-cell>
      }

      <!-- Main Schema Column Headers -->
      @for (column of displayedColumns(); track column.field) {
        <hx-header-cell
          [align]="column.align"
          [sortDirection]="getMainTableSortDirection(column.field)"
          [sortable]="column.sortable || false"
          [tooltip]="column.header"
          [width]="column.width"
          (sort)="column.sortable ? sortMainTable(column.field, $event) : undefined">
          <div class="hx-managed-table__header-content">
            @if (column.filterable) {
              <button
                class="hx-managed-table__filter-button"
                type="button"
                [attr.aria-label]="'Filter ' + column.header"
                [backdrop]="true"
                [hxPopover]="filterPopover"
                [position]="'bottom-start'"
                [trigger]="'click'">
                <hx-icon
                  class="hx-managed-table__filter-icon"
                  [filled]="hasActiveFilter(column.field)"
                  [inline]="true"
                  [svgIcon]="'funnel'" />
              </button>

              <!-- Filter Popover Template -->
              <ng-template #filterPopover>
                <div class="hx-managed-table__filter-popover">
                  <hx-input
                    [autofocus]="true"
                    [placeholder]="column.filterPlaceholder || 'Filter...'"
                    [value]="getFilterValue(column.field)"
                    (valueChange)="applyFilter(column.field, $event.value)" />

                  @if (hasActiveFilter(column.field)) {
                    <button class="hx-managed-table__clear-filter" type="button" (click)="clearFilter(column.field)">
                      Clear filter
                    </button>
                  }
                </div>
              </ng-template>
            }

            <span class="hx-managed-table__header-text">{{ column.header }}</span>
          </div>
        </hx-header-cell>
      }
    </hx-table-header>

    <!-- Table Rows - Always use hx-expandable-row for consistent projection -->
    @for (row of paginatedData(); track row.id) {
      <hx-expandable-row [expanded]="isExpanded(row.id)" (expandedChange)="toggleExpansion(row.id)">
        <!-- Trigger cell (only if table is expandable) -->
        @if (config().expandable) {
          <hx-table-cell class="hx-managed-table__trigger-cell">
            <button
              class="hx-managed-table__trigger"
              type="button"
              [attr.aria-expanded]="isExpanded(row.id)"
              [attr.aria-label]="isExpanded(row.id) ? 'Collapse row' : 'Expand row'"
              (click)="toggleExpansion(row.id)">
              <hx-icon [fontIcon]="isExpanded(row.id) ? 'chevron-up' : 'chevron-down'" />
            </button>
          </hx-table-cell>
        }

        <!-- Main row data cells -->
        @for (column of displayedColumns(); track column.field; let i = $index) {
          <hx-table-cell [align]="column.align">
            @if (column.cellRenderer) {
              <ng-container *ngComponentOutlet="column.cellRenderer; inputs: cellRendererInputs(row, column, i)" />
            } @else {
              <span class="hx-managed-table__cell-text" [title]="row.data[column.field]">
                {{ row.data[column.field] }}
              </span>
            }
          </hx-table-cell>
        }

        <!-- Expandable sub-table content (only if expandable and has sub-data) -->
        @if (config().expandable && row.subData && row.subData.length > 0) {
          <ng-container expandableContent>
            <hx-table
              class="hx-managed-table__sub-table"
              maxHeight=""
              [headerVariant]="headerVariant()"
              [rowVariant]="rowVariant()">
              <!-- Sub-Table Header -->
              <hx-table-header>
                @for (column of config().subSchema; track column.field) {
                  <hx-header-cell
                    [align]="column.align"
                    [sortDirection]="getSubTableSortDirection(row.id, column.field)"
                    [sortable]="column.sortable || false"
                    [width]="column.width"
                    [tooltip]="column.header"
                    (sort)="column.sortable ? sortSubTable(row.id, column.field, $event) : undefined">
                    {{ column.header }}
                  </hx-header-cell>
                }
              </hx-table-header>

              <!-- Sub-Table Rows -->
              @for (subRow of getSubTableData(row); track $index; let subIndex = $index) {
                <hx-table-row class="hx-managed-table__sub-row">
                  @for (column of config().subSchema; track column.field) {
                    <hx-table-cell [align]="column.align">
                      @if (column.cellRenderer) {
                        <ng-container
                          *ngComponentOutlet="
                            column.cellRenderer;
                            inputs: cellRendererInputs({ id: row.id, data: subRow }, column, subIndex, row.id)
                          " />
                      } @else {
                        <span class="hx-managed-table__cell-text" [title]="subRow[column.field]">
                          {{ subRow[column.field] }}
                        </span>
                      }
                    </hx-table-cell>
                  }
                </hx-table-row>
              }
            </hx-table>
          </ng-container>
        }
      </hx-expandable-row>
    }

    <!-- Pagination Footer -->
    @if (enablePagination() && hasData()) {
      <hx-table-footer>
        <hx-pagination
          class="hx-managed-table__pagination"
          [disabled]="disabled()"
          [pageSizeOptions]="pageSizeOptions()"
          [totalRecordCount]="totalRecordCount()"
          [(currentPage)]="currentPage"
          [(pageSize)]="pageSize" />
      </hx-table-footer>
    }
  </hx-table>
} @else {
  <div class="hx-managed-table__no-results">
    <hx-table-empty-state [message]="noDataMessage()" />
  </div>
}
